import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Refresh design : profondeur douce + léger lift au survol (vie sans changer la couleur attendue).
        default: "bg-primary text-primary-foreground shadow-[var(--elev-1)] hover:bg-primary/90 hover:shadow-[var(--elev-2)] hover:-translate-y-px active:translate-y-0",
        destructive:
          "bg-destructive text-white shadow-[var(--elev-1)] hover:bg-destructive/90 hover:-translate-y-px focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-[var(--elev-1)] hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        // Secondaire légèrement teinté violet (fin du gris terne).
        secondary: "bg-[color-mix(in_srgb,var(--color-kestio-violet)_9%,var(--secondary))] text-[var(--color-kestio-violet)] hover:bg-[color-mix(in_srgb,var(--color-kestio-violet)_16%,var(--secondary))]",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        // Dégradé « signature » — CTA à forte énergie (opt-in, n'affecte aucun bouton existant).
        signature: "bg-[image:var(--grad-signature)] text-white shadow-[0_4px_14px_rgba(90,75,190,.35)] hover:-translate-y-px hover:shadow-[0_7px_20px_rgba(90,75,190,.45)]",
        // Dégradé « duel » (étape 8).
        duel: "bg-[image:var(--grad-duel)] text-white shadow-[0_6px_18px_rgba(192,66,63,.4)] hover:-translate-y-px hover:shadow-[0_9px_24px_rgba(192,66,63,.5)]",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
